import { useTranslation } from 'react-i18next';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { isFieldRestricted } from '../../fields/fieldRestricted';
import { FieldRenderer } from '../../fields/FieldRenderer';
import { RestrictedValue } from '../../fields/RestrictedValue';
import { useTitlePlaceholder } from '../header/useTitlePlaceholder';
import { valueOf } from '../valueOf';
import type { IUnderTitleProps } from './IUnderTitleProps';
import { UnderTitleTags } from './UnderTitleTags';

/**
 * Поля під вкладками на «Загальному» (UIUX forms/form-structure; прототип v4) у порядку `form.underTitle` визначення: назва — поле 42px
 * з підписом платформи «Назва» (прототип: «Title» для всіх типів, не підпис поля типу, напр. «Мандат»; рамка 1.5px, радіус 10, 16/600,
 * * для обов'язкової), теги — поле-рамка з чипами, опис — текст від 96px (14px, міжрядковий 1.55, росте за вмістом), інші поля — на всю
 * ширину. Поля, яких визначення тут не розміщує, не показуються. Поле з персональними даними, приховане сервером (зокрема опис), — без
 * значення й без редагування (RestrictedValue).
 */
export function UnderTitleFields({ ws, rt, record, editor, readOnly }: IUnderTitleProps) {
  const { t } = useTranslation();
  const strings = useModuleStrings();
  const placeholder = useTitlePlaceholder(rt);
  return (
    <>
      {rt.form.underTitle.map((key) => {
        const field = rt.fields.find((f) => f.key === key);
        if (!field) return null;
        const label = key === 'title' ? t('form.title') : strings.field(rt.key, key, field.label);
        const error = editor.fieldErrors[key];
        if (key === 'title') {
          return (
            <div key={key} className="rec-ut">
              <label className="field__label fc-label" htmlFor="rec-title-field">{label}{!rt.titleAuto && <span className="req"> *</span>}</label>
              <input id="rec-title-field" className="control control--title" value={String(valueOf(record, editor.draft, 'title') ?? '')} readOnly={readOnly}
                aria-required={!rt.titleAuto} aria-invalid={Boolean(error) || undefined} onChange={(e) => editor.setField('title', e.target.value)}
                placeholder={placeholder} />
              {error && <span className="field__error">{error}</span>}
            </div>
          );
        }
        if (field.type === 'tags') return <UnderTitleTags key={key} ws={ws} record={record} editor={editor} readOnly={readOnly} label={label} />;
        const restricted = isFieldRestricted(record, field);
        if (field.type === 'textarea' && restricted) {
          return (
            <div key={key} className="rec-ut rec-ut--desc">
              <label className="field__label fc-label" htmlFor={`rec-${key}`}>{label}</label>
              <RestrictedValue id={`rec-${key}`} />
            </div>
          );
        }
        if (field.type === 'textarea') {
          return (
            <div key={key} className="rec-ut rec-ut--desc">
              <label className="field__label fc-label" htmlFor={`rec-${key}`}>{label}</label>
              <textarea id={`rec-${key}`} className="control rec-ut__text" value={String(valueOf(record, editor.draft, key) ?? '')} readOnly={readOnly}
                placeholder={t('recordPage.general.descriptionPlaceholder')} onChange={(e) => editor.setField(key, e.target.value)} />
              {error && <span className="field__error">{error}</span>}
            </div>
          );
        }
        return (
          <div key={key} className="rec-ut">
            <FieldRenderer field={field} rt={rt} ws={ws} record={record} width="L" value={valueOf(record, editor.draft, key)} onChange={editor.setField} readOnly={readOnly} error={error}
              restricted={restricted} />
          </div>
        );
      })}
    </>
  );
}
